<template>
  <div class="search-container">
    <div class="p-grid">
      <div class="p-col">
        <div class="p-input-icon-right search-input">
          <i
            class="pi pi-search"
            style="left: 0.5rem !important"
            v-if="!chips.length"
          />
          <i
            class="pi pi-angle-down"
            style="cursor: pointer"
            @click="$refs.searchFilters.toggle($event)"
          />
          <Chips
            type="text"
            style="width: 100%"
            :class="[
              {
                'search-input-placeholder-active': !chips.length,
              },
            ]"
            :placeholder="!chips.length ? 'Pesquisar' : ''"
            separator=","
            :modelValue="chips"
            @keyup.enter="search()"
            @add="addTerm($event)"
            @remove="removeChip($event)"
          />

          <div id="search-options" style="width: 95%"></div>

          <SearchContainer
            v-if="isMounted"
            ref="searchFilters"
            :dismissable="false"
          >
            <div
              class="p-fluid search-options-fields"
              :style="
                optionsHeight
                  ? { maxHeight: optionsHeight, paddingRight: '10px' }
                  : {}
              "
            >
              <!-- Tipo -->
              <div class="p-field p-grid">
                <label class="p-col-4">Tipo</label>
                <div class="p-col-8">
                  <Dropdown
                    v-model="searchFilters.type.value"
                    :options="searchFilters.type.options"
                    optionLabel="name"
                    appendTo="body"
                    style="width: 100%"
                  />
                </div>
              </div>
              <!-- Categoria -->
              <div class="p-field p-grid">
                <label class="p-col-4">
                  <span class="material-icons search-option-icon p-mr-3"
                    >category</span
                  >
                  Categoria</label
                >
                <div class="p-col-8">
                  <MultiSelect
                    @click="fetchOptions('category')"
                    v-model="searchFilters.category.value"
                    :options="searchFilters.category.options"
                    optionLabel="name"
                    emptyFilterMessage="Nenhum resultado"
                    appendTo="body"
                    scrollHeight="100px"
                    filter
                  >
                  </MultiSelect>
                </div>
              </div>
              <!-- Valor -->
              <div class="p-field p-grid">
                <label class="p-col-4">
                  <span
                    class="material-icons search-option-icon p-mr-3"
                    style="font-size: 22px"
                  >
                    attach_money
                  </span>
                  Valor
                </label>
                <div class="p-col-4">
                  <InputNumber
                    v-model="searchFilters.value.value[0]"
                    :minFractionDigits="2"
                    mode="currency"
                    currency="BRL"
                    locale="pt-BR"
                  />
                </div>
                <div class="p-col-4">
                  <InputNumber
                    v-model="searchFilters.value.value[1]"
                    :minFractionDigits="2"
                    mode="currency"
                    currency="BRL"
                    locale="pt-BR"
                  />
                </div>
              </div>
              <!-- Data -->
              <div class="p-field p-grid">
                <label class="p-col-4">
                  <span class="material-icons search-option-icon p-mr-3">
                    event
                  </span>
                  Data entre
                </label>
                <div class="p-col-4">
                  <Calendar
                    v-model="searchFilters.date.value[0]"
                    dateFormat="dd/mm/yy"
                    showIcon
                    :monthNavigator="true"
                    :yearNavigator="true"
                    :yearRange="dateRange"
                    :locale="$locales.calendar.ptBR"
                    appendTo="body"
                  />
                </div>
                <div class="p-col-4">
                  <Calendar
                    v-model="searchFilters.date.value[1]"
                    dateFormat="dd/mm/yy"
                    showIcon
                    :monthNavigator="true"
                    :yearNavigator="true"
                    :yearRange="dateRange"
                    :locale="$locales.calendar.ptBR"
                    appendTo="body"
                  />
                </div>
              </div>
              <!-- Fazenda -->
              <div class="p-field p-grid">
                <label class="p-col-4">
                  <span class="material-icons search-option-icon p-mr-3"
                    >map</span
                  >
                  Fazenda
                </label>
                <div class="p-col-8">
                  <MultiSelect
                    @click="fetchOptions('farms')"
                    v-model="searchFilters.farms.value"
                    :options="searchFilters.farms.options"
                    optionLabel="name"
                    emptyFilterMessage="Nenhum resultado"
                    appendTo="body"
                    scrollHeight="120px"
                    filter
                  >
                  </MultiSelect>
                </div>
              </div>
            </div>
            <div class="p-fluid" style="margin-top: 20px">
              <div class="p-grid p-jc-end">
                <div class="p-col-4">
                  <Button label="Pesquisar" @click="search()" />
                </div>
              </div>
            </div>
          </SearchContainer>
        </div>
      </div>
      <div class="p-col-3">
        <Dropdown
          v-model="searchOrder.orderField"
          :options="searchOrder.options"
          optionLabel="name"
          placeholder="Ordenar por"
          @change="search()"
          style="width: 100%"
        />
      </div>
      <div class="p-col-fixed search-order" style="width: 50px">
        <Button
          :icon="orderIcon"
          class="p-button-text p-button-plain"
          @click="changeOrder()"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { debounce } from 'debounce';
import {
  buildQuery,
  getChipValue,
  useIdsFilter,
  useTermFilter,
  useDateRangeFilter,
  useValueRangeFilter,
} from '../../../lib/search';
import { CashFlowCategoryApi, FarmApi } from '../../../services/api';
import SearchContainer from '../../../components/SearchContainer.vue';

function useTypeFilter() {
  return {
    value: '',
    options: [
      { name: 'Receita', value: 1 },
      { name: 'Despesa', value: 2 },
    ],
    name: 'Tipo',
    query: (value) => ({ typeId: value.value }),
    chips: (name, value) => [getChipValue(name, value.name)],
    removeValue: () => '',
  };
}

export default {
  name: 'CashFlowSearch',
  props: {
    optionsHeight: {
      type: [Number, String],
      default: () => '70vh',
    },
  },
  components: {
    SearchContainer,
  },
  data() {
    return {
      dateRange: `${this.$dayjs()
        .subtract(10, 'y')
        .format('YYYY')}:${this.$dayjs().add(10, 'y').format('YYYY')}`,
      chips: [],
      searchFilters: {
        term: useTermFilter(),
        type: useTypeFilter(),
        category: useIdsFilter(
          'Categoria',
          'categoryId',
          'name',
          CashFlowCategoryApi.fetch
        ),
        farms: useIdsFilter('Fazenda', 'farmId', 'name', FarmApi.fetch),
        value: useValueRangeFilter('Valor', 'value'),
        date: useDateRangeFilter('Data', 'date'),
      },
      searchOrder: {
        orderField: null,
        order: '',
        options: [
          { name: 'Nome', value: 'name' },
          { name: 'Data', value: 'date' },
          { name: 'Valor', value: 'value' },
        ],
      },
      isMounted: false,
    };
  },
  async created() {
    const { chips } = this.buildQuery();
    this.chips = chips;
  },
  mounted() {
    this.isMounted = true;
  },
  computed: {
    orderIcon() {
      if (!this.searchOrder.order) {
        return 'pi pi-sort-alt';
      }
      return this.searchOrder.order === 'asc'
        ? 'pi pi-sort-amount-up'
        : 'pi pi-sort-amount-down';
    },
  },
  methods: {
    async fetchOptions(field) {
      const option = this.searchFilters[field];
      if (option && option.fetchOptions && !option.options.length) {
        option.options = await option.fetchOptions({}, this.searchFilters);
      }
    },
    addTerm({ value }) {
      const term = value
        .filter((v) => !v.includes(':') || v.includes('Termo: '))
        .map((v) => v.replace('Termo: ', ''));
      this.searchFilters.term.value = term;
    },
    removeChip(event) {
      const [name, value] = event.value[0].split(': ');
      const field = Object.keys(this.searchFilters).find(
        (key) => this.searchFilters?.[key]?.name === name
      );
      const searchFilter = this.searchFilters[field];
      if (searchFilter.removeValue) {
        this.searchFilters[field].value = searchFilter.removeValue(
          searchFilter.value,
          value
        );
      }
      this.search();
    },
    changeOrder() {
      this.searchOrder.order =
        this.searchOrder.order === 'asc' ? 'desc' : 'asc';
      this.search();
    },
    buildQuery() {
      return buildQuery(this.searchFilters, this.searchOrder);
    },
    search: debounce(
      function search() {
        const { query, chips } = this.buildQuery();
        this.chips = chips;
        this.$emit('search', { query });
        this.$refs.searchFilters.hide();
      },
      500,
      true
    ),
  },
};
</script>

<style lang="scss" scoped>
.search-container {
  ::v-deep .p-overlaypanel {
    left: 0 !important;
  }
  ::v-deep .p-overlaypanel:after,
  .p-overlaypanel:before {
    left: calc(var(--overlayArrowLeft, 0) + 0.6rem);
  }
  ::v-deep .p-overlaypanel:before {
    left: calc(var(--overlayArrowLeft, 0) + 0.6rem);
  }
}
.search-option-icon {
  font-size: 18px;
  color: #495057;
}
.search-options-fields {
  overflow-y: auto;
  overflow-x: hidden;
}
.search-input {
  width: 100%;
  ::v-deep .p-chips-multiple-container {
    width: 100%;
    display: inline;
  }
}
.search-input-placeholder-active {
  ::v-deep input {
    padding-left: 1.5rem !important;
  }
}
.search-order {
  button {
    background-color: white;
  }
}
.search-order .p-button {
  border: 1px solid #ced4da !important;
  border-radius: 3px !important;
}
@media (max-width: 600px) {
  .search-container .p-col {
    flex-grow: unset !important;
    flex-basis: unset !important;
    width: 100%;
  }
  .search-container .p-col-3 {
    flex-grow: 1;
    flex-basis: 0;
  }
  .search-order {
    width: 47px !important;
  }
}
</style>
